import { useEffect, useState } from 'react'

import {
  RIBBON_CONFIG,
  type RibbonTuning,
} from '../ribbon/config'
import {
  type RibbonDiagnostics,
  RibbonScene,
} from '../ribbon/RibbonScene'

interface DebugPanelProps {
  controller: RibbonScene | null
}

interface RangeControlProps<Key extends keyof RibbonTuning> {
  controller: RibbonScene | null
  label: string
  max: number
  min: number
  property: Key
  step: number
  value: RibbonTuning[Key]
}

const EMPTY_DIAGNOSTICS: RibbonDiagnostics = {
  clipped: false,
  currentProgress: 0,
  fps: 0,
  frameCorrections: 0,
  frameMedianMs: 0,
  frameP95Ms: 0,
  maxConstraintError: 0,
  minAdjacentFrameDot: 1,
  ndcExtentX: 0,
  ndcExtentY: 0,
  reducedMotion: false,
  renderCalls: 0,
  slowFrameRatio: 0,
  targetProgress: 0,
  triangles: 0,
}

function RangeControl<Key extends keyof RibbonTuning>({
  controller,
  label,
  max,
  min,
  property,
  step,
  value,
}: RangeControlProps<Key>): React.JSX.Element {
  const [displayValue, setDisplayValue] = useState(Number(value))

  return (
    <label className="debug-control">
      <span>
        {label}
        <output>{displayValue.toFixed(step < 0.1 ? 3 : 1)}</output>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        defaultValue={Number(value)}
        disabled={!controller}
        onInput={(event) => {
          const nextValue = Number(event.currentTarget.value)
          setDisplayValue(nextValue)
          controller?.updateTuning(property, nextValue)
        }}
      />
    </label>
  )
}

export function DebugPanel({
  controller,
}: DebugPanelProps): React.JSX.Element | null {
  const [diagnostics, setDiagnostics] =
    useState<RibbonDiagnostics>(EMPTY_DIAGNOSTICS)

  useEffect(() => {
    if (!controller) {
      return
    }

    const interval = window.setInterval(() => {
      setDiagnostics(controller.getDiagnostics())
    }, 500)

    return () => {
      window.clearInterval(interval)
    }
  }, [controller])

  if (!import.meta.env.DEV) {
    return null
  }

  return (
    <details className="debug-panel">
      <summary aria-label="Open ribbon tuning panel">Tune</summary>
      <div className="debug-panel__body">
        <header>
          <strong>Ribbon study</strong>
          <span>development</span>
        </header>

        <label className="debug-control">
          <span>
            Scroll progress
            <output>{diagnostics.targetProgress.toFixed(3)}</output>
          </span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.001"
            value={diagnostics.targetProgress}
            disabled={!controller}
            onChange={(event) => {
              window.__RIBBON_DEBUG__?.setProgress(
                Number(event.currentTarget.value),
              )
            }}
          />
        </label>

        <div className="debug-stops" aria-label="Canonical progress states">
          {[0, 0.25, 0.5, 0.75, 1].map((progress) => (
            <button
              key={progress}
              type="button"
              onClick={() => {
                window.__RIBBON_DEBUG__?.setProgress(progress)
              }}
            >
              {Math.round(progress * 100)}
            </button>
          ))}
        </div>

        <RangeControl
          controller={controller}
          label="Turbulence"
          property="turbulenceAmplitude"
          min={0}
          max={0.24}
          step={0.005}
          value={RIBBON_CONFIG.tuning.turbulenceAmplitude}
        />
        <RangeControl
          controller={controller}
          label="Noise scale"
          property="turbulenceScale"
          min={0.1}
          max={1.2}
          step={0.01}
          value={RIBBON_CONFIG.tuning.turbulenceScale}
        />
        <RangeControl
          controller={controller}
          label="Fabric"
          property="fabricStrength"
          min={0}
          max={0.5}
          step={0.01}
          value={RIBBON_CONFIG.tuning.fabricStrength}
        />
        <RangeControl
          controller={controller}
          label="Guide pull"
          property="guideStiffness"
          min={40}
          max={400}
          step={5}
          value={RIBBON_CONFIG.tuning.guideStiffness}
        />
        <RangeControl
          controller={controller}
          label="Progress damping"
          property="progressDamping"
          min={1}
          max={14}
          step={0.1}
          value={RIBBON_CONFIG.tuning.progressDamping}
        />
        <RangeControl
          controller={controller}
          label="Shadow"
          property="shadowOpacity"
          min={0}
          max={0.5}
          step={0.01}
          value={RIBBON_CONFIG.tuning.shadowOpacity}
        />

        <label className="debug-toggle">
          <span>Reduced motion</span>
          <input
            type="checkbox"
            checked={diagnostics.reducedMotion}
            disabled={!controller}
            onChange={(event) => {
              controller?.setReducedMotion(event.currentTarget.checked)
            }}
          />
        </label>

        <dl className="debug-metrics">
          <div>
            <dt>Current</dt>
            <dd>{diagnostics.currentProgress.toFixed(3)}</dd>
          </div>
          <div>
            <dt>FPS</dt>
            <dd>{diagnostics.fps.toFixed(0)}</dd>
          </div>
          <div>
            <dt>Frame</dt>
            <dd>{diagnostics.frameMedianMs.toFixed(1)} ms</dd>
          </div>
          <div>
            <dt>P95</dt>
            <dd>{(diagnostics.frameP95Ms ?? 0).toFixed(1)} ms</dd>
          </div>
          <div>
            <dt>Slow &gt;25</dt>
            <dd>{((diagnostics.slowFrameRatio ?? 0) * 100).toFixed(0)}%</dd>
          </div>
          <div>
            <dt>Frame dot</dt>
            <dd>{diagnostics.minAdjacentFrameDot.toFixed(3)}</dd>
          </div>
          <div>
            <dt>Frame flips</dt>
            <dd>{diagnostics.frameCorrections}</dd>
          </div>
          <div>
            <dt>Constraint</dt>
            <dd>{diagnostics.maxConstraintError.toFixed(4)}</dd>
          </div>
          <div>
            <dt>Bounds</dt>
            <dd>
              {diagnostics.ndcExtentX.toFixed(2)} ×{' '}
              {diagnostics.ndcExtentY.toFixed(2)}
            </dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{diagnostics.clipped ? 'clipped' : 'clear'}</dd>
          </div>
          <div>
            <dt>Motion</dt>
            <dd>{diagnostics.reducedMotion ? 'reduced' : 'live'}</dd>
          </div>
        </dl>
      </div>
    </details>
  )
}
